/**
 * Copyright (c) 2010-2022 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanLoad,
    Params,
    Route,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable } from 'rxjs';
import { AuthState } from '../../security/state/authState.state';
import { Select, Store } from '@ngxs/store';
import { concatMapTo, filter, first, map, take, tap } from 'rxjs/operators';
import { LoggerService } from '../../../modules/shared/services/logger.service';
import { RequestedRoute } from '../../security/state/authState.model';
import { RedirectToLogin } from '../../security/state/authState.actions';
import { User } from '../../../types';
import { AuthService } from '../../security/services/auth.service';
import { AuthenticatedGuard } from './authenticated.guard';

/**
 * Apply this guard only to resources that are restricted to Admin users
 */
@Injectable()
export class AdminAreaGuard implements CanActivate, CanLoad {
    @Select(AuthState.user) private user$: Observable<User>;

    constructor(
        private logger: LoggerService,
        private authenticatedGuard: AuthenticatedGuard,
        private auth: AuthService
    ) {}

    canLoad(route: Route): Observable<boolean> {
        return this.authenticatedGuard.canLoad(route).pipe(
            filter((b) => b),
            concatMapTo(this.auth.isAdmin$()),
            take(1)
        );
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.authenticatedGuard.canActivate(route, state).pipe(
            filter((b) => b),
            concatMapTo(this.auth.isAdmin$()),
            take(1)
        );
    }
}
